import { RETURN_CONDITIONS, RETURN_REASONS, SUPPLY_CLASSES } from '@wodi/domain';
import { z } from 'zod';

import { tenderSchema } from './billing.ts';
import { amountSchema } from './money.ts';

// The pharmacy: item search, the counter sale, returns, and item supply class and barcodes
// (docs/plans/phase1-dispensing.md §5; ADR 0014). Quantities are whole base units. Search
// terms travel in POST bodies; no patient data travels in a URL or query string.

const quantitySchema = z.number().int().min(1).max(100_000);
const reasonSchema = z.string().trim().min(3, 'Say why, in a few words').max(500);
const shortText = (max: number) => z.string().trim().min(1).max(max);

export const batchChoiceSchema = z.object({ batchId: z.uuid(), quantity: quantitySchema });

/** A batch as the pharmacy shows it: number (mono) and expiry. */
export const batchPlanSchema = z.object({
  batchId: z.uuid(),
  batchNumber: z.string(),
  expiryDate: z.iso.date().nullable(),
  quantity: z.number().int().min(1),
  isFefo: z.boolean(),
});
export type BatchPlanView = z.infer<typeof batchPlanSchema>;

export const pharmacyStoreQuerySchema = z.object({ storeId: z.uuid() });

// --- Item search ----------------------------------------------------------------------------

export const pharmacyItemSearchBodySchema = z.object({
  storeId: z.uuid(),
  /** A name, a code or a scanned barcode. */
  query: z.string().trim().min(1).max(100),
});

export const pharmacyItemSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  baseUnitDisplay: z.string(),
  supplyClass: z.enum(SUPPLY_CLASSES),
  isControlled: z.boolean(),
  /** Usable stock at the store now: in date, in base units. */
  usable: z.number().int().min(0),
  /** The cash price per base unit, or null with the reason it has none. */
  cashPrice: amountSchema.nullable(),
  unpricedReason: z.string().nullable(),
  /** When the search matched a barcode: the pack it names (null is the base unit). */
  scanned: z
    .object({ packUnitCode: z.string().nullable(), baseUnitsPerPack: z.number().int().min(1) })
    .nullable(),
});
export type PharmacyItemView = z.infer<typeof pharmacyItemSchema>;

export const pharmacyItemListSchema = z.object({ items: z.array(pharmacyItemSchema) });

// --- The counter sale -----------------------------------------------------------------------

export const saleLineInputSchema = z.object({
  serviceItemId: z.uuid(),
  quantity: quantitySchema,
  /** The pack scanned to add this line, if one was. */
  packCode: z.string().trim().min(1).max(40).optional(),
  /** Batches chosen instead of the earliest-expiring; needs `overrideReason` if different. */
  batches: z.array(batchChoiceSchema).min(1).max(20).optional(),
  overrideReason: reasonSchema.optional(),
  /** Label instructions, required for prescription-only lines. */
  instructions: z.string().trim().min(3).max(500).optional(),
});

export const COUNCIL_SUGGESTIONS = ['KMPDC', 'COC', 'NCK', 'PPB', 'Other'] as const;

export const externalPrescriptionInputSchema = z.object({
  prescriberName: shortText(120),
  /** As typed; TODO(verify-spec): valid councils (plan §18 C3). */
  prescriberCouncil: shortText(40),
  prescriberRegistrationNumber: shortText(40),
  issuingFacilityName: shortText(160),
  issuingFacilityMflCode: z.string().trim().max(20).optional(),
  prescribedOn: z.iso.date(),
  note: z.string().trim().max(500).optional(),
});

export const salePreviewBodySchema = z.object({
  storeId: z.uuid(),
  lines: z
    .array(saleLineInputSchema.omit({ instructions: true }))
    .min(1)
    .max(50),
});

export const salePreviewLineSchema = z.object({
  lineNo: z.number().int().min(1),
  serviceItemId: z.uuid(),
  quantity: z.number().int(),
  /** The batches the sale would take, earliest expiry first; empty when it can't be covered. */
  batches: z.array(batchPlanSchema),
  usable: z.number().int().min(0),
  unitPrice: amountSchema.nullable(),
  lineTotal: amountSchema.nullable(),
  /** In words, when the line can't be sold as it is. */
  problem: z.string().nullable(),
});

export const salePreviewSchema = z.object({
  lines: z.array(salePreviewLineSchema),
  total: amountSchema,
  /** True when every line can be sold now. */
  ready: z.boolean(),
});
export type SalePreviewView = z.infer<typeof salePreviewSchema>;

export const createSaleBodySchema = z.object({
  clientRequestId: z.uuid(),
  storeId: z.uuid(),
  /** The identified buyer; required for prescription-only lines. */
  patientId: z.uuid().optional(),
  externalPrescription: externalPrescriptionInputSchema.optional(),
  lines: z.array(saleLineInputSchema).min(1).max(50),
  tenders: z.array(tenderSchema).min(1).max(4),
});
export type CreateSaleBody = z.infer<typeof createSaleBodySchema>;

export const saleViewSchema = z.object({
  id: z.uuid(),
  saleNumber: z.string(),
  storeId: z.uuid(),
  visitId: z.uuid(),
  patient: z.object({ id: z.uuid(), name: z.string(), patientNumber: z.string() }).nullable(),
  completedAt: z.iso.datetime(),
  completedBy: z.object({ id: z.uuid(), name: z.string() }),
  hasExternalPrescription: z.boolean(),
  lines: z.array(
    z.object({
      id: z.uuid(),
      lineNo: z.number().int(),
      serviceItemId: z.uuid(),
      itemName: z.string(),
      baseUnitDisplay: z.string(),
      supplyClass: z.enum(SUPPLY_CLASSES),
      quantity: z.number().int(),
      instructions: z.string().nullable(),
      batches: z.array(batchPlanSchema),
      amount: amountSchema.nullable(),
    }),
  ),
  total: amountSchema,
  receipts: z.array(z.object({ id: z.uuid(), number: z.string() })),
  change: amountSchema.nullable(),
});
export type SaleView = z.infer<typeof saleViewSchema>;

export const saleListQuerySchema = z.object({
  storeId: z.uuid().optional(),
  /** An East Africa calendar day; defaults to today. */
  day: z.iso.date().optional(),
});

export const saleListItemSchema = z.object({
  id: z.uuid(),
  saleNumber: z.string(),
  completedAt: z.iso.datetime(),
  completedBy: z.string(),
  lineCount: z.number().int(),
  total: amountSchema,
  identified: z.boolean(),
});
export const saleListSchema = z.object({ items: z.array(saleListItemSchema) });
export type SaleListItem = z.infer<typeof saleListItemSchema>;

// --- Returns ----------------------------------------------------------------------------------

export const RETURN_SOURCES = ['dispense', 'sale'] as const;

export const returnableQuerySchema = z.object({
  sourceType: z.enum(RETURN_SOURCES),
  /** A dispense or sale number, as printed (mono). Sent in the body, never the URL. */
  number: z.string().trim().min(1).max(40),
});

export const returnableSchema = z.object({
  sourceType: z.enum(RETURN_SOURCES),
  sourceId: z.uuid(),
  sourceNumber: z.string(),
  storeId: z.uuid(),
  givenAt: z.iso.datetime(),
  patient: z.object({ id: z.uuid(), name: z.string(), patientNumber: z.string() }).nullable(),
  lines: z.array(
    z.object({
      sourceLineId: z.uuid(),
      itemName: z.string(),
      baseUnitDisplay: z.string(),
      isControlled: z.boolean(),
      batchId: z.uuid(),
      batchNumber: z.string(),
      expiryDate: z.iso.date().nullable(),
      given: z.number().int(),
      alreadyReturned: z.number().int(),
      returnable: z.number().int(),
      /** An expired batch or a controlled item can never go back on the shelf. */
      mustNotRestock: z.boolean(),
    }),
  ),
});
export type ReturnableView = z.infer<typeof returnableSchema>;

export const createReturnBodySchema = z.object({
  clientRequestId: z.uuid(),
  sourceType: z.enum(RETURN_SOURCES),
  sourceId: z.uuid(),
  reasonCode: z.enum(RETURN_REASONS),
  note: z.string().trim().max(500).optional(),
  lines: z
    .array(
      z.object({
        sourceLineId: z.uuid(),
        batchId: z.uuid(),
        quantity: quantitySchema,
        condition: z.enum(RETURN_CONDITIONS),
      }),
    )
    .min(1)
    .max(50),
});

export type CreateReturnBody = z.infer<typeof createReturnBodySchema>;

export const returnDecisionBodySchema = z.object({ note: z.string().trim().max(500).optional() });

export const RETURN_STATUSES = ['requested', 'approved', 'rejected', 'withdrawn'] as const;

export const returnViewSchema = z.object({
  id: z.uuid(),
  returnNumber: z.string(),
  sourceType: z.enum(RETURN_SOURCES),
  sourceId: z.uuid(),
  sourceNumber: z.string(),
  reasonCode: z.enum(RETURN_REASONS),
  note: z.string().nullable(),
  status: z.enum(RETURN_STATUSES),
  requestedBy: z.object({ id: z.uuid(), name: z.string() }),
  requestedAt: z.iso.datetime(),
  decidedBy: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  decidedAt: z.iso.datetime().nullable(),
  decisionNote: z.string().nullable(),
  canDecide: z.boolean(),
  canWithdraw: z.boolean(),
  lines: z.array(
    z.object({
      lineNo: z.number().int(),
      sourceLineId: z.uuid(),
      itemName: z.string(),
      baseUnitDisplay: z.string(),
      batchNumber: z.string(),
      expiryDate: z.iso.date().nullable(),
      quantity: z.number().int(),
      condition: z.enum(RETURN_CONDITIONS),
      restocked: z.boolean(),
    }),
  ),
  /** What approving does to the bill, in words, for the approver. */
  billEffect: z.string(),
});
export type ReturnView = z.infer<typeof returnViewSchema>;

export const returnListQuerySchema = z.object({ status: z.enum(RETURN_STATUSES).optional() });
export const returnListSchema = z.object({ items: z.array(returnViewSchema) });

// --- Item settings ----------------------------------------------------------------------------

export const supplyClassBodySchema = z.object({
  supplyClass: z.enum(SUPPLY_CLASSES),
  reason: reasonSchema,
});

export const barcodesBodySchema = z.object({
  barcodes: z
    .array(
      z.object({
        barcode: z
          .string()
          .trim()
          .regex(/^[0-9A-Za-z-]{4,64}$/, 'Letters, digits and dashes'),
        packUnitCode: z.string().trim().min(1).max(40).nullable(),
      }),
    )
    .max(20),
});

export const itemPharmacySettingsSchema = z.object({
  serviceItemId: z.uuid(),
  code: z.string(),
  name: z.string(),
  status: z.enum(['active', 'inactive']),
  baseUnitDisplay: z.string(),
  /** The item's packs a barcode can name; switched-off packs are left out. */
  packUnits: z.array(
    z.object({ code: z.string(), display: z.string(), baseUnitsPerPack: z.number().int() }),
  ),
  supplyClass: z.enum(SUPPLY_CLASSES),
  isControlled: z.boolean(),
  barcodes: z.array(z.object({ barcode: z.string(), packUnitCode: z.string().nullable() })),
});
export type ItemPharmacySettingsView = z.infer<typeof itemPharmacySettingsSchema>;

/** Settings > Pharmacy items: stock items with their supply class, by name, code or barcode. */
export const itemPharmacySettingsSearchBodySchema = z.object({
  query: z.string().trim().max(100).default(''),
  supplyClass: z.enum(SUPPLY_CLASSES).optional(),
  limit: z.number().int().min(1).max(100).default(50),
});

export const itemPharmacySettingsListSchema = z.object({
  items: z.array(itemPharmacySettingsSchema),
});
